import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

// 🎨 GLOBAL COLORS DEFINITION
const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: '#6366F1',
    secondary: '#8B5CF6',
    accent: '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    gradientMain: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
};

function AllDesignerTasks() {
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [refreshing, setRefreshing] = useState(false);
    const [deletingTask, setDeletingTask] = useState(null);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    const userRole = sessionStorage.getItem('userRole');
    const userId = sessionStorage.getItem('userId');
    const userName = sessionStorage.getItem('userName') || 'User';

    useEffect(() => {
        // Check if user has permission to view this page
        if (!['admin', 'manager'].includes(userRole)) {
            navigate('/dashboard');
            return;
        }
        fetchAllDesignerTasks();
    }, []);

    useEffect(() => {
        filterTasks();
    }, [tasks, statusFilter, searchTerm]);

    const fetchAllDesignerTasks = async () => {
        try {
            if (!refreshing) setLoading(true);
            setError('');
            setMessage('');

            const response = await fetch(`${API_BASE}/designer_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_all_designer_tasks'
                })
            });

            const text = await response.text();
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                throw new Error('Invalid JSON response from server');
            }
            
            if (!response.ok || !data.success) {
                throw new Error(data.error || `Error loading tasks: HTTP status ${response.status}`);
            }
            
            setTasks(data.tasks || []);

        } catch (error) {
            console.error('Error fetching designer tasks:', error);
            setError('Error loading tasks: ' + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // ✅ ADDED: Delete task function for designer tasks
    const handleDeleteTask = async (taskId, taskName) => {
        console.log('🗑️ Delete button clicked - Designer Task ID:', taskId, 'Task Name:', taskName);
        console.log('📋 Available designer task IDs:', tasks.map(t => ({ id: t.id, name: t.video_name || t.description })));
        
        if (!window.confirm(`Are you sure you want to delete designer task: "${taskName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            setDeletingTask(taskId);
            setMessage('Deleting designer task...');
            setError('');
            
            console.log('🔄 Sending delete request for designer task ID:', taskId);
            
            // Note: You might need to create a separate API for designer tasks deletion
            // For now, using the same deletetask.php but you might need to adjust it
            const response = await fetch(`${API_BASE}/deletetask.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    task_id: taskId,
                    // Add a flag to indicate it's a designer task if needed
                    task_type: 'designer'
                })
            });

            const data = await response.json();
            console.log('📥 Delete API response:', data);
            
            if (data.success) {
                setMessage(`✅ Designer task "${taskName}" deleted successfully!`);
                
                // Update local state by removing the deleted task
                setTasks(prevTasks => {
                    const updatedTasks = prevTasks.filter(task => task.id !== taskId);
                    console.log('📊 Designer tasks after deletion:', updatedTasks);
                    return updatedTasks;
                });
                
                // Clear message after 3 seconds
                setTimeout(() => {
                    setMessage('');
                }, 3000);
            } else {
                // Show detailed error message from API
                console.error('❌ Delete failed:', data);
                throw new Error(data.message || 'Failed to delete designer task');
            }
        } catch (error) {
            console.error('❌ Error deleting designer task:', error);
            setError(`❌ Error: ${error.message}`);
            
            // Refresh tasks to see current state
            setTimeout(() => {
                fetchAllDesignerTasks();
            }, 2000);
        } finally {
            setDeletingTask(null);
        }
    };

    const filterTasks = () => {
        let filtered = tasks;

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(task => task.status === statusFilter);
        }

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(task => 
                (task.video_name && task.video_name.toLowerCase().includes(term)) ||
                (task.description && task.description.toLowerCase().includes(term)) ||
                (task.designer_name && task.designer_name.toLowerCase().includes(term)) ||
                (task.assigned_by_name && task.assigned_by_name.toLowerCase().includes(term))
            );
        }

        setFilteredTasks(filtered);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchAllDesignerTasks();
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
    };

    // Get status badge with proper styling
    const getStatusBadge = (status) => {
        const statusConfig = {
            todo: { class: 'bg-secondary text-white', text: 'To Do', icon: 'fas fa-clock' },
            inprogress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fas fa-palette' },
            completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fas fa-check-circle' },
            approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fas fa-thumbs-up' },
            needs_revision: { class: 'bg-danger text-white', text: 'Needs Revision', icon: 'fas fa-redo' }
        };
        
        const config = statusConfig[status] || statusConfig.todo;
        return (
            <span className={`badge ${config.class}`}>
                <i className={`${config.icon} me-1`}></i>
                {config.text}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'NULL' || dateString === 'null') return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString || dateString === 'NULL' || dateString === 'null') return 'Not available';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if link is valid
    const isValidLink = (link) => {
        if (!link || typeof link !== 'string') return false;
        const invalidValues = ['-', 'Open', 'NULL', 'null', ''];
        return !invalidValues.includes(link.trim());
    };

    // Handle link click
    const handleLinkClick = (link) => {
        if (isValidLink(link)) {
            const formattedLink = link.startsWith('http') ? link : `https://${link}`;
            window.open(formattedLink, '_blank');
        }
    };

    // Handle edit task - Navigate to edit page
    const handleEditTask = (task) => {
        // Store task data in sessionStorage for the edit page
        sessionStorage.setItem('editTaskData', JSON.stringify(task));
        navigate('/update-designer-task');
    };

    if (loading) {
        return (
            <Layout>
                <div style={{minHeight: '100vh', background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div style={{textAlign: 'center', color: 'white'}}>
                        <div className="loading-dots" style={styles.loadingDots}>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                        </div>
                        <h3 style={styles.loadingText}>Loading Designer Tasks</h3>
                        <p style={styles.loadingSubtext}>Fetching all designer tasks</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{minHeight: '100vh'}}>
                <div className="designer-tasks-container" style={styles.container}>
                    {/* Animated Background */}
                    <div className="animated-background" style={styles.animatedBackground}>
                        <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                        <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                        <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                        <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                        <div className="grid-overlay" style={styles.gridOverlay}></div>
                    </div>

                    {/* Header Section */}
                    <div className="designer-tasks-header" style={styles.header}>
                        <div className="header-content" style={styles.headerContent}>
                            <div className="welcome-section">
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-palette me-2"></i>
                                    All Designer Tasks
                                </h1>
                                <p className="page-subtitle" style={styles.pageSubtitle}>
                                    Complete overview of all designer tasks
                                </p>
                            </div>
                            <div className="header-actions" style={styles.headerActions}>
                                <button 
                                    className="refresh-btn"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    style={styles.refreshBtn}
                                >
                                    <div className="refresh-icon" style={styles.refreshIcon}>
                                        <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                    </div>
                                    <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Success Message */}
                    {message && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: `1px solid ${colors.success}`,
                            color: colors.success
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, color: colors.success}}>
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <span style={{color: 'white'}}>{message}</span>
                            </div>
                            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: 'rgba(220, 53, 69, 0.1)',
                            border: `1px solid ${colors.danger}`,
                            color: colors.danger
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <span style={{color: 'white'}}>{error}</span>
                            </div>
                            <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {/* Main Content - Only Table */}
                    <div className="designer-tasks-content" style={styles.content}>
                        {/* Filters and Search */}
                        <div className="filters-section" style={styles.filtersSection}>
                            <div className="search-card" style={styles.searchCard}>
                                <div className="card-body" style={styles.cardBody}>
                                    <div className="row align-items-center">
                                        <div className="col-md-6 mb-3 mb-md-0">
                                            <div className="input-group">
                                                <span className="input-group-text" style={styles.inputGroupText}>
                                                    <i className="fas fa-search" style={{color: colors.textLight}}></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={styles.searchInput}
                                                    placeholder="Search tasks by description, designer, or assigned by..."
                                                    value={searchTerm}
                                                    onChange={handleSearch}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                                {['all', 'todo', 'inprogress', 'completed'].map((status) => (
                                                    <button 
                                                        key={status}
                                                        className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                                                        onClick={() => handleStatusFilter(status)}
                                                        style={{
                                                            ...styles.filterBtn,
                                                            ...(statusFilter === status ? styles.filterBtnActive : {})
                                                        }}
                                                    >
                                                        {status === 'all' && 'All'}
                                                        {status === 'todo' && 'To Do'}
                                                        {status === 'inprogress' && 'In Progress'}
                                                        {status === 'completed' && 'Completed'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tasks Table Only */}
                        <div className="tasks-section" style={styles.tasksSection}>
                            <div className="section-header" style={styles.sectionHeader}>
                                <h3 style={{color: 'white'}}>
                                    {statusFilter === 'all' && 'All Designer Tasks'}
                                    {statusFilter === 'todo' && 'To Do Designer Tasks'}
                                    {statusFilter === 'inprogress' && 'Designer Tasks In Progress'}
                                    {statusFilter === 'completed' && 'Completed Designer Tasks'}
                                </h3>
                                <span className="tasks-count" style={styles.tasksCount}>
                                    {filteredTasks.length} tasks
                                </span>
                            </div>
                            
                            <div className="tasks-card" style={styles.tasksCard}>
                                {filteredTasks.length === 0 ? (
                                    <div className="empty-state" style={styles.emptyState}>
                                        <div className="empty-icon" style={styles.emptyIcon}>
                                            <i className="fas fa-palette"></i>
                                        </div>
                                        <h3 style={styles.emptyTitle}>No designer tasks found</h3>
                                        <p style={styles.emptyText}>
                                            {searchTerm 
                                                ? 'No tasks match your search criteria'
                                                : `No ${statusFilter !== 'all' ? statusFilter : ''} designer tasks found`
                                            }
                                        </p>
                                        {searchTerm && (
                                            <button 
                                                className="clear-search-btn"
                                                onClick={() => setSearchTerm('')}
                                                style={styles.clearSearchBtn}
                                            >
                                                Clear Search
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="table-container" style={styles.tableContainer}>
                                        <table className="tasks-table" style={styles.tasksTable}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.tableHeader}>Task Name</th>
                                                    <th style={styles.tableHeader}>Description</th>
                                                    <th style={styles.tableHeader}>Status</th>
                                                    <th style={styles.tableHeader}>Raw Link</th>
                                                    <th style={styles.tableHeader}>Designer Name</th>
                                                    <th style={styles.tableHeader}>Thumbnail URL</th>
                                                    <th style={styles.tableHeader}>Submitted URL</th>
                                                    <th style={styles.tableHeader}>Designer Notes</th>
                                                    <th style={{...styles.tableHeader, width: '180px'}}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredTasks.map((task) => (
                                                    <tr key={task.id} style={styles.tableRow}>
                                                        <td style={styles.tableCell}>
                                                            <strong style={{color: 'white'}}>
                                                                {task.video_name || 'Untitled Task'}
                                                            </strong>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={{color: colors.textLight}}>
                                                                {task.description || 'No description'}
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {getStatusBadge(task.status)}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {isValidLink(task.raw_link) ? (
                                                                <button 
                                                                    className="link-btn"
                                                                    onClick={() => handleLinkClick(task.raw_link)}
                                                                    style={styles.linkBtn}
                                                                >
                                                                    <i className="fas fa-link me-1"></i>
                                                                    Raw Link
                                                                </button>
                                                            ) : (
                                                                <span style={styles.noLink}>No Link</span>
                                                            )}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={{color: 'white', fontWeight: '600'}}>
                                                                {task.designer_name || 'Unknown'}
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {isValidLink(task.thumbnail_url) ? (
                                                                <button 
                                                                    className="link-btn"
                                                                    onClick={() => handleLinkClick(task.thumbnail_url)}
                                                                    style={styles.linkBtn}
                                                                >
                                                                    <i className="fas fa-image me-1"></i>
                                                                    Thumbnail
                                                                </button>
                                                            ) : (
                                                                <span style={styles.noLink}>No Thumbnail</span>
                                                            )}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {isValidLink(task.submitted_url) ? (
                                                                <button 
                                                                    className="link-btn"
                                                                    onClick={() => handleLinkClick(task.submitted_url)}
                                                                    style={styles.linkBtn}
                                                                >
                                                                    <i className="fas fa-link me-1"></i>
                                                                    Submitted
                                                                </button>
                                                            ) : (
                                                                <span style={styles.noLink}>No URL</span>
                                                            )}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={{color: colors.textLight}}>
                                                                {task.designer_notes && task.designer_notes !== 'NULL'
                                                                    ? (task.designer_notes.length > 50 
                                                                        ? `${task.designer_notes.substring(0, 50)}...`
                                                                        : task.designer_notes)
                                                                    : 'No notes'
                                                                }
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                <button 
                                                                    className="edit-btn"
                                                                    onClick={() => handleEditTask(task)}
                                                                    style={styles.editBtn}
                                                                    title="Edit Task"
                                                                >
                                                                    <i className="fas fa-edit me-1"></i>
                                                                    Edit
                                                                </button>
                                                                <button 
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteTask(task.id, task.video_name || task.description || 'Untitled Task')}
                                                                    disabled={deletingTask === task.id}
                                                                    style={{
                                                                        ...styles.deleteBtn,
                                                                        opacity: deletingTask === task.id ? 0.7 : 1
                                                                    }}
                                                                    title="Delete Task"
                                                                >
                                                                    {deletingTask === task.id ? (
                                                                        <>
                                                                            <div style={{
                                                                                width: '12px',
                                                                                height: '12px',
                                                                                border: '2px solid white',
                                                                                borderTop: '2px solid transparent',
                                                                                borderRadius: '50%',
                                                                                animation: 'spin 1s linear infinite',
                                                                                marginRight: '5px'
                                                                            }}></div>
                                                                            Deleting...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fas fa-trash me-1"></i>
                                                                            Delete
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <style jsx>{`
                    .spinning {
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    @keyframes float {
                        0%, 100% { transform: translate(0px, 0px) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }

                    @keyframes wave {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }

                    @keyframes bounce {
                        0%, 80%, 100% {
                            transform: scale(0);
                        } 40% {
                            transform: scale(1);
                        }
                    }
                    
                    /* Custom Hover Effects */
                    .edit-btn:hover {
                        transform: translateY(-2px);
                    }
                    .delete-btn:hover {
                        transform: translateY(-2px);
                    }
                    .filter-btn:hover {
                        transform: translateY(-1px);
                    }
                    .table-row:hover {
                        background: rgba(255, 255, 255, 0.05);
                    }
                `}</style>
            </div>
        </Layout>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    pageTitle: {
        fontSize: '2.5rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: '0 0 10px 0'
    },
    pageSubtitle: {
        color: colors.textLight,
        fontSize: '1.1rem',
        margin: 0,
        maxWidth: '600px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '30px'
    },
    refreshBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    refreshIcon: {
        fontSize: '1.1rem'
    },
    messageAlert: {
        color: 'white',
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alertIcon: {
        fontSize: '1.5rem'
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    filtersSection: {
        marginBottom: '30px'
    },
    searchCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)'
    },
    cardBody: {
        padding: '20px'
    },
    inputGroupText: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: `1px solid ${colors.borderDark}`,
        borderRight: 'none',
        color: colors.textLight
    },
    searchInput: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: `1px solid ${colors.borderDark}`,
        borderLeft: 'none',
        color: 'white',
        backdropFilter: 'blur(10px)'
    },
    filterBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: colors.textLight,
        border: `1px solid ${colors.borderDark}`,
        padding: '8px 16px',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)'
    },
    filterBtnActive: {
        background: colors.primary,
        color: 'white',
        borderColor: colors.primary
    },
    tasksSection: {
        marginBottom: '40px'
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    tasksCount: {
        background: colors.primary,
        color: 'white',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '0.85rem',
        fontWeight: '600'
    },
    tasksCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: colors.textLight
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.5
    },
    emptyTitle: {
        color: 'white',
        marginBottom: '10px'
    },
    emptyText: {
        margin: '0 0 20px 0'
    },
    clearSearchBtn: {
        background: colors.primary,
        color: 'white',
        border: 'none',
        padding: '8px 20px',
        borderRadius: '20px',
        cursor: 'pointer',
        fontWeight: '600'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    tasksTable: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    tableHeader: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '15px 12px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.85rem',
        borderBottom: `2px solid ${colors.borderDark}`,
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'background 0.3s ease'
    },
    tableCell: {
        padding: '12px',
        borderBottom: `1px solid rgba(255, 255, 255, 0.03)`,
        verticalAlign: 'top'
    },
    linkBtn: {
        background: colors.primary,
        color: 'white',
        border: 'none',
        padding: '6px 12px',
        borderRadius: '15px',
        fontSize: '0.8rem',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center'
    },
    noLink: {
        color: colors.textLight,
        fontSize: '0.85rem'
    },
    editBtn: {
        background: colors.warning,
        color: 'white',
        border: 'none',
        padding: '6px 12px',
        borderRadius: '15px',
        fontSize: '0.8rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'inline-flex',
        alignItems: 'center'
    },
    deleteBtn: {
        background: colors.danger,
        color: 'white',
        border: 'none',
        padding: '6px 12px',
        borderRadius: '15px',
        fontSize: '0.8rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'inline-flex',
        alignItems: 'center'
    },
    loadingDots: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '20px'
    },
    dot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: colors.primary,
        animation: 'bounce 1.4s ease-in-out infinite both'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600',
        color: 'white'
    },
    loadingSubtext: {
        color: colors.textLight,
        margin: 0,
        fontSize: '1.1rem'
    }
};

export default AllDesignerTasks;